import { ContactUsType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { submitContactForm } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import BlobLayout from "@/components/layout/BlobLayout"

export const Route = createFileRoute("/contact-us")({
  component: ContactUs,
})

export default function ContactUs(data: ContactUsType) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const formSchema = z.object({
    company_name: z
      .string()
      .min(2, "Company name must be at least 2 characters."),
    name: z.string().min(2, "Name must be at least 2 characters."),
    email: z.string().email("Please enter a valid email address."),
    phone_number: z.string().regex(/^\d{11,}$/, {
      message: "Phone number must have at least 11 digits.",
    }),
    landline: z.string().nullable(),
    message: z.string().min(10, "Message must be at least 10 characters."),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: data.company_name,
      name: data.name,
      email: data.email,
      phone_number: data.phone_number,
      landline: data.landline,
      message: data.message,
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ContactUsType) => {
      await submitContactForm(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactUsDetails"] })
      toast({
        description: "Form submitted successfully.",
      })
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as ContactUsType)
  }

  return (
    <BlobLayout>
      <div className="container">
        <Card className="shadow-inner-custom z-10 w-full rounded-2xl text-[#00B7AE] lg:w-[40vw]">
          <CardContent>
            <CardHeader className="text-center font-gilroy text-3xl font-bold">
              Contact Us
            </CardHeader>
            <div className="flex flex-col items-center">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="grid grid-cols-2 gap-4 lg:container"
                >
                  <FormField
                    control={form.control}
                    name="company_name"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Company Name
                        </FormLabel>
                        <FormControl>
                          <Input className="border-[#00B7AE]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Name
                        </FormLabel>
                        <FormControl>
                          <Input className="border-[#00B7AE]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input className="border-[#00B7AE]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Phone Number
                        </FormLabel>
                        <FormControl>
                          <Input className="border-[#00B7AE]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="landline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Landline
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="border-[#00B7AE]"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel className="font-gilroy font-bold text-black dark:text-white">
                          Message
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            className="resize-none border-[#00B7AE]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <CardFooter className="col-span-2 flex flex-row items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      className="text-[#179195] ring-1 ring-[#179195]"
                      onClick={() => navigate({ to: "/" })}
                    >
                      Back to Home
                    </Button>
                    <Button type="submit" className="w-[120px]">
                      Submit
                    </Button>
                  </CardFooter>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </BlobLayout>
  )
}
